import React, { useState } from 'react';
import './Dashboard.css';

const Dashboard = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);

  // Mock data for demonstration
  const stats = {
    totalOrders: 1247,
    totalRevenue: 45680,
    totalProducts: 89,
    totalCustomers: 2341
  };

  const recentOrders = [
    { id: '#1234', customer: 'John Doe', product: 'Rose Bouquet', amount: '$45.99', status: 'Delivered', date: '2024-01-15' },
    { id: '#1235', customer: 'Jane Smith', product: 'Mixed Flowers', amount: '$32.50', status: 'Processing', date: '2024-01-14' },
    { id: '#1236', customer: 'Mike Johnson', product: 'Lily Arrangement', amount: '$28.75', status: 'Shipped', date: '2024-01-13' },
  ];

  const products = [
    { id: 1, name: 'Rose Bouquet', price: '$45.99', stock: 25, category: 'Bouquets', status: 'Active' },
    { id: 2, name: 'Mixed Flowers', price: '$32.50', stock: 18, category: 'Bouquets', status: 'Active' },
    { id: 3, name: 'Lily Arrangement', price: '$28.75', stock: 0, category: 'Arrangements', status: 'Out of Stock' },
  ];

  const customers = [
    { id: 1, name: 'John Doe', email: 'john@example.com', orders: 5, totalSpent: '$234.50', joinDate: '2023-06-15' },
    { id: 2, name: 'Jane Smith', email: 'jane@example.com', orders: 3, totalSpent: '$156.75', joinDate: '2023-08-22' },
    { id: 3, name: 'Mike Johnson', email: 'mike@example.com', orders: 8, totalSpent: '$445.20', joinDate: '2023-05-10' },
  ];

  const sidebarItems = [
    { id: 'overview', label: 'Overview', icon: '📊' },
    { id: 'orders', label: 'Orders', icon: '📦' },
    { id: 'products', label: 'Products', icon: '🌹' },
    { id: 'customers', label: 'Customers', icon: '👥' },
    { id: 'analytics', label: 'Analytics', icon: '📈' },
    { id: 'settings', label: 'Settings', icon: '⚙️' },
    { id: 'users', label: 'User Management', icon: '👤' },
    { id: 'inventory', label: 'Inventory', icon: '📋' },
    { id: 'promotions', label: 'Promotions', icon: '🎯' },
    { id: 'reports', label: 'Reports', icon: '📄' }
  ];

  const renderOverview = () => (
    <div className="dashboard-overview">
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon">📦</div>
          <div className="stat-content">
            <h3>{stats.totalOrders.toLocaleString()}</h3>
            <p>Total Orders</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">💰</div>
          <div className="stat-content">
            <h3>${stats.totalRevenue.toLocaleString()}</h3>
            <p>Total Revenue</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">🌹</div>
          <div className="stat-content">
            <h3>{stats.totalProducts}</h3>
            <p>Total Products</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">👥</div>
          <div className="stat-content">
            <h3>{stats.totalCustomers.toLocaleString()}</h3>
            <p>Total Customers</p>
          </div>
        </div>
      </div>

      <div className="recent-orders">
        <h2>Recent Orders</h2>
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Order ID</th>
                <th>Customer</th>
                <th>Product</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {recentOrders.map(order => (
                <tr key={order.id}>
                  <td>{order.id}</td>
                  <td>{order.customer}</td>
                  <td>{order.product}</td>
                  <td>{order.amount}</td>
                  <td><span className={`status ${order.status.toLowerCase()}`}>{order.status}</span></td>
                  <td>{order.date}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderOrders = () => (
    <div className="dashboard-section">
      <div className="section-header">
        <h2>Order Management</h2>
        <button className="btn-primary">Add New Order</button>
      </div>
      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>Order ID</th>
              <th>Customer</th>
              <th>Product</th>
              <th>Amount</th>
              <th>Status</th>
              <th>Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {recentOrders.map(order => (
              <tr key={order.id}>
                <td>{order.id}</td>
                <td>{order.customer}</td>
                <td>{order.product}</td>
                <td>{order.amount}</td>
                <td><span className={`status ${order.status.toLowerCase()}`}>{order.status}</span></td>
                <td>{order.date}</td>
                <td>
                  <button className="btn-small">Edit</button>
                  <button className="btn-small btn-danger">Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderProducts = () => (
    <div className="dashboard-section">
      <div className="section-header">
        <h2>Product Management</h2>
        <button className="btn-primary">Add New Product</button>
      </div>
      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>ID</th>
              <th>Name</th>
              <th>Price</th>
              <th>Stock</th>
              <th>Category</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {products.map(product => (
              <tr key={product.id}>
                <td>{product.id}</td>
                <td>{product.name}</td>
                <td>{product.price}</td>
                <td>{product.stock}</td>
                <td>{product.category}</td>
                <td><span className={`status ${product.status.toLowerCase().replace(' ', '-')}`}>{product.status}</span></td>
                <td>
                  <button className="btn-small">Edit</button>
                  <button className="btn-small btn-danger">Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderCustomers = () => (
    <div className="dashboard-section">
      <div className="section-header">
        <h2>Customer Management</h2>
        <button className="btn-primary">Add New Customer</button>
      </div>
      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>ID</th>
              <th>Name</th>
              <th>Email</th>
              <th>Orders</th>
              <th>Total Spent</th>
              <th>Join Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {customers.map(customer => (
              <tr key={customer.id}>
                <td>{customer.id}</td>
                <td>{customer.name}</td>
                <td>{customer.email}</td>
                <td>{customer.orders}</td>
                <td>{customer.totalSpent}</td>
                <td>{customer.joinDate}</td>
                <td>
                  <button className="btn-small">Edit</button>
                  <button className="btn-small btn-danger">Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderAnalytics = () => (
    <div className="dashboard-section">
      <h2>Analytics & Reports</h2>
      <div className="analytics-grid">
        <div className="chart-container">
          <h3>Sales Overview</h3>
          <div className="chart-placeholder">
            📈 Sales Chart Placeholder
          </div>
        </div>
        <div className="chart-container">
          <h3>Top Products</h3>
          <div className="chart-placeholder">
            📊 Product Performance Chart
          </div>
        </div>
        <div className="chart-container">
          <h3>Customer Growth</h3>
          <div className="chart-placeholder">
            👥 Customer Growth Chart
          </div>
        </div>
        <div className="chart-container">
          <h3>Revenue Trends</h3>
          <div className="chart-placeholder">
            💰 Revenue Chart
          </div>
        </div>
      </div>
    </div>
  );

  const renderSettings = () => (
    <div className="dashboard-section">
      <h2>System Settings</h2>
      <div className="settings-grid">
        <div className="setting-card">
          <h3>General Settings</h3>
          <div className="setting-item">
            <label>Website Name</label>
            <input type="text" defaultValue="Tulipink" />
          </div>
          <div className="setting-item">
            <label>Default Currency</label>
            <select defaultValue="USD">
              <option value="USD">USD</option>
              <option value="EUR">EUR</option>
              <option value="GBP">GBP</option>
            </select>
          </div>
          <div className="setting-item">
            <label>Time Zone</label>
            <select defaultValue="UTC">
              <option value="UTC">UTC</option>
              <option value="EST">EST</option>
              <option value="PST">PST</option>
            </select>
          </div>
        </div>

        <div className="setting-card">
          <h3>Email Settings</h3>
          <div className="setting-item">
            <label>SMTP Server</label>
            <input type="text" placeholder="smtp.gmail.com" />
          </div>
          <div className="setting-item">
            <label>Email Address</label>
            <input type="email" placeholder="admin@tulipink.com" />
          </div>
          <div className="setting-item">
            <label>Email Password</label>
            <input type="password" placeholder="••••••••" />
          </div>
        </div>

        <div className="setting-card">
          <h3>Payment Settings</h3>
          <div className="setting-item">
            <label>Stripe Public Key</label>
            <input type="text" placeholder="pk_test_..." />
          </div>
          <div className="setting-item">
            <label>Stripe Secret Key</label>
            <input type="password" placeholder="sk_test_..." />
          </div>
          <div className="setting-item">
            <label>PayPal Client ID</label>
            <input type="text" placeholder="PayPal Client ID" />
          </div>
        </div>

        <div className="setting-card">
          <h3>Security Settings</h3>
          <div className="setting-item">
            <label>Session Timeout (minutes)</label>
            <input type="number" defaultValue="30" />
          </div>
          <div className="setting-item">
            <label>Password Requirements</label>
            <select defaultValue="strong">
              <option value="basic">Basic</option>
              <option value="medium">Medium</option>
              <option value="strong">Strong</option>
            </select>
          </div>
          <div className="setting-item">
            <label>Two-Factor Authentication</label>
            <input type="checkbox" defaultChecked />
          </div>
        </div>
      </div>
      <div className="settings-actions">
        <button className="btn-primary">Save Settings</button>
        <button className="btn-secondary">Reset to Default</button>
      </div>
    </div>
  );

  const renderContent = () => {
    switch (activeTab) {
      case 'overview': return renderOverview();
      case 'orders': return renderOrders();
      case 'products': return renderProducts();
      case 'customers': return renderCustomers();
      case 'analytics': return renderAnalytics();
      case 'settings': return renderSettings();
      default: return renderOverview();
    }
  };

  return (
    <div className="dashboard">
      <div className={`dashboard-sidebar ${isSidebarOpen ? 'open' : 'closed'}`}>
        <div className="sidebar-header">
          <h2>Admin Panel</h2>
          <button 
            className="sidebar-toggle"
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
          >
            {isSidebarOpen ? '←' : '→'}
          </button>
        </div>
        
        <nav className="sidebar-nav">
          {sidebarItems.map(item => (
            <button
              key={item.id}
              className={`nav-item ${activeTab === item.id ? 'active' : ''}`}
              onClick={() => setActiveTab(item.id)}
            >
              <span className="nav-icon">{item.icon}</span>
              <span className="nav-label">{item.label}</span>
            </button>
          ))}
        </nav>
      </div>

      <div className="dashboard-main">
        <div className="dashboard-header">
          <h1>Dashboard</h1>
          <div className="header-actions">
            <button className="btn-secondary">Export Data</button>
            <button className="btn-primary">Refresh</button>
          </div>
        </div>

        <div className="dashboard-content">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
